import React, { useState, useEffect } from 'react';
import { Trophy, Shield, Target, UserPlus, Trash2, Zap } from 'lucide-react';
import '../../css/SmashBountyTracker.css'; // Make sure this matches your CSS filename!

const SmashBountyTracker = () => {
  const [players, setPlayers] = useState(() => {
    const saved = localStorage.getItem('smash-bounty-data');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [newPlayerName, setNewPlayerName] = useState('');
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [winnerId, setWinnerId] = useState(null);
  const [loserId, setLoserId] = useState(null);

  useEffect(() => {
    localStorage.setItem('smash-bounty-data', JSON.stringify(players));
  }, [players]);

  const addPlayer = () => {
    if (!newPlayerName) return;
    const newPlayer = {
      id: Date.now(),
      name: newPlayerName,
      pp: 1,
      vp: 0,
      ap: 0
    };
    setPlayers([...players, newPlayer]);
    setNewPlayerName('');
  };

  const resolveMatch = () => {
    if (winnerId === null || loserId === null || winnerId === loserId) return;

    setPlayers(prev => prev.map(p => {
      const winner = prev.find(p => p.id === winnerId);
      const loser = prev.find(p => p.id === loserId);

      if (p.id === winnerId) {
        return { 
          ...p, 
          vp: p.vp + loser.pp, 
          pp: p.pp + 1 
        };
      }
      if (p.id === loserId) {
        return { 
          ...p, 
          ap: p.ap + (winner.pp + 1),
          pp: winner.pp > p.pp ? p.pp : 1
        };
      }
      return p;
    }));
    
    setWinnerId(null);
    setLoserId(null);
    setShowMatchModal(false);
  };

  const spendAP = (playerId, amount) => {
    setPlayers(prev => prev.map(p => 
      p.id === playerId ? { ...p, ap: Math.max(0, p.ap - amount) } : p
    ));
  };

  return (
    <div className="bounty-container">
      <header className="header">
        <div>
          <h1 className="title">Bounty Hunt</h1>
          <p className="subtitle">Smash Ultimate Event Tracker</p>
        </div>
        
        <div className="controls">
          <a 
            href="/Smash_Bounty_Hunt_Rules.pdf" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="rules-link"
          >
            📖 View Rules
          </a>
          <input 
            type="text" 
            value={newPlayerName}
            onChange={(e) => setNewPlayerName(e.target.value)}
            placeholder="Add Fighter..."
            className="player-input"
          />
          <button onClick={addPlayer} className="add-btn">
            <UserPlus size={24} />
          </button>
        </div>
      </header>

      <div className="player-grid">
        {players.sort((a,b) => b.vp - a.vp).map(player => (
          <div key={player.id} className="player-card">
            <div className="card-header">
              <h2 className="player-name">{player.name}</h2>
              <button onClick={() => setPlayers(players.filter(p => p.id !== player.id))} className="delete-btn">
                <Trash2 size={18} />
              </button>
            </div>

            <div className="stats-grid">
              <div>
                <div className="stat-icon icon-target"><Target size={20} /></div>
                <div className="stat-value">{player.pp}</div>
                <div className="stat-label">Bounty (PP)</div>
              </div>
              <div>
                <div className="stat-icon icon-trophy"><Trophy size={20} /></div>
                <div className="stat-value">{player.vp}</div>
                <div className="stat-label">Score (VP)</div>
              </div>
              <div>
                <div className="stat-icon icon-shield"><Shield size={20} /></div>
                <div className="stat-value">{player.ap}</div>
                <div className="stat-label">Bank (AP)</div>
              </div>
            </div>

            <div className="shop-buttons">
              <button onClick={() => spendAP(player.id, 1)} className="shop-btn">1AP Stage/Ban</button>
              <button onClick={() => spendAP(player.id, 2)} className="shop-btn">2AP Stock/Merc</button>
              <button onClick={() => spendAP(player.id, 3)} className="shop-btn">3AP Random</button>
              <button onClick={() => spendAP(player.id, 4)} className="shop-btn">4AP Target</button>
              <button onClick={() => spendAP(player.id, 5)} className="shop-btn">5AP Double Stock</button>
              <button onClick={() => spendAP(player.id, 10)} className="shop-btn btn-heist">10AP Heist</button>
            </div>
          </div>
        ))}
      </div>

      <button 
        onClick={() => setShowMatchModal(true)}
        className="fab-btn"
      >
        <Zap fill="white" /> RESOLVE MATCH
      </button>

      {showMatchModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Match Result</h2>
            
            <div>
              <label className="stat-label" style={{display: 'block', marginBottom: '0.5rem'}}>The Winner</label>
              <select 
                className="match-select"
                onChange={(e) => setWinnerId(Number(e.target.value))}
              >
                <option>Select Winner...</option>
                {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              <div className="vs-text">VS</div>

              <label className="stat-label" style={{display: 'block', marginBottom: '0.5rem'}}>The Loser</label>
              <select 
                className="match-select"
                onChange={(e) => setLoserId(Number(e.target.value))}
              >
                <option>Select Loser...</option>
                {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              <div className="modal-actions">
                <button 
                  onClick={() => setShowMatchModal(false)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => resolveMatch()}
                  className="btn-confirm"
                >
                  Confirm Win
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmashBountyTracker;